// Simplified Supabase PUMS Service - Fetch ALL Data
import { supabaseClient } from '../../../services/supabase';

export interface FullPumaRecord {
  serialno: string;
  puma: number;
  state: number;
  adjinc: number;
  wgtp: number;
  np: number;
  bld: number;
  elep: number;
  fulp: number;
  gasp: number;
  hfl: number;
  rntp: number;
  ten: number;
  valp: number;
  veh: number;
  yrblt: number;
  fincp: number;
  grpip: number;
  hincp: number;
  lngi: number;
  pwtgp: number;
  agep: number;
  eng: number;
  jwtrns: number;
  relshipp: number;
  schl: number;
  sex: number;
  dis: number;
  hisp: number;
  pincp: number;
  rac1p: number;
  // Add any other columns that exist in your table
}

export class SimplePumsService {
  /**
   * Get ALL data from Supabase table - no filtering, just raw records
   * Uses pagination to ensure we get every single record
   */
  async getAllPumaData(): Promise<FullPumaRecord[]> {
    try {
      console.log('🔍 Fetching ALL PUMA data from Supabase (with optimized pagination)...');
      
      let allData: any[] = [];
      let page = 0;
      const pageSize = 50000; // Increased from 10k to 50k for faster loading
      let hasMore = true;

      while (hasMore) {
        const startTime = Date.now();
        console.log(`📄 Fetching page ${page + 1} (records ${page * pageSize + 1} to ${(page + 1) * pageSize})...`);
        
        const { data, error } = await supabaseClient
          .from('pums_households_bayarea')
          .select('*')
          .eq('state', 6)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error) {
          console.error('❌ Supabase query error:', error);
          console.error('Error details:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }

        const fetchTime = Date.now() - startTime;
        
        if (!data || data.length === 0) {
          hasMore = false;
          console.log(`📄 Page ${page + 1}: No more data`);
        } else {
          allData = allData.concat(data);
          console.log(`📄 Page ${page + 1}: Fetched ${data.length} records in ${fetchTime}ms (total: ${allData.length})`);
          
          // If we got fewer records than pageSize, we've reached the end
          if (data.length < pageSize) {
            hasMore = false;
          }
          
          page++;
        }
      }

      console.log(`📊 Total fetched: ${allData.length} complete records from Supabase`);

      if (allData.length === 0) {
        console.log('⚠️ No data returned from Supabase');
        return [];
      }

      // Log the first record to see what columns we have
      console.log('📋 Sample record structure:', Object.keys(allData[0]));
      console.log('📋 Sample record data:', allData[0]);

      // Return all data as-is
      const results: FullPumaRecord[] = allData.map((row: any) => ({
        serialno: row.serialno,
        puma: row.puma,
        state: row.state,
        adjinc: row.adjinc,
        wgtp: row.wgtp,
        np: row.np,
        bld: row.bld,
        elep: row.elep,
        fulp: row.fulp,
        gasp: row.gasp,
        hfl: row.hfl,
        rntp: row.rntp,
        ten: row.ten,
        valp: row.valp,
        veh: row.veh,
        yrblt: row.yrblt,
        fincp: row.fincp,
        grpip: row.grpip,
        hincp: row.hincp,
        lngi: row.lngi,
        pwtgp: row.pwtgp,
        agep: row.agep,
        eng: row.eng,
        jwtrns: row.jwtrns,
        relshipp: row.relshipp,
        schl: row.schl,
        sex: row.sex,
        dis: row.dis,
        hisp: row.hisp,
        pincp: row.pincp,
        rac1p: row.rac1p,
      }));

      console.log(`✅ Loaded ${results.length} complete records`);
      return results;
    } catch (error) {
      console.error('❌ Error fetching all PUMA data:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        throw new Error(`Failed to fetch all PUMA data: ${error.message}`);
      }
      throw new Error('Failed to fetch all PUMA data');
    }
  }

  /**
   * Get column information and unique values for filter building
   */
  async getColumnInfo(): Promise<{ [key: string]: any[] }> {
    try {
      console.log('🔍 Analyzing column data for filter building...');
      
      const allData = await this.getAllPumaData();
      
      if (allData.length === 0) {
        return {};
      }

      const columnInfo: { [key: string]: any[] } = {};
      
      // Get unique values for each column
      Object.keys(allData[0]).forEach(column => {
        const uniqueValues = Array.from(new Set(allData.map(record => record[column as keyof FullPumaRecord])));
        columnInfo[column] = uniqueValues.sort((a, b) => {
          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }
          return String(a).localeCompare(String(b));
        });
      });

      console.log('📊 Column analysis complete:', Object.keys(columnInfo));
      return columnInfo;
    } catch (error) {
      console.error('❌ Error analyzing column data:', error);
      throw new Error('Failed to analyze column data');
    }
  }
}

// Export singleton instance
export const simplePumsService = new SimplePumsService();
